import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { http, api } from "@/lib/api";
import { sortPlayersDKOrder } from "@/lib/dkRoster";
import {
  ArrowsClockwise, UploadSimple, DownloadSimple, Lightning, Fire,
  CheckCircle, WarningCircle,
} from "@phosphor-icons/react";

/**
 * Sprint 60.100 · Late Swap
 *
 * Full flow:
 *   1. User pastes / drops a DK entries CSV (exported from DK's
 *      Manage Entries UI). We parse each row's entry_id + 9 slot cells.
 *   2. Kickoff column drives auto-lock detection against the user's
 *      browser wall clock (native tz + America/New_York fallback).
 *      User can override the lock checklist manually.
 *   3. (Optional) user enters live scores per player for lineups
 *      already in progress. Portfolio median vs slate median then
 *      decides chase vs comeback mode.
 *   4. Click "Run Late Swap" — backend `/late-swap/analyze` returns
 *      per-lineup swap recommendations (injury sweep always on;
 *      chase or comeback picks the second signal).
 *   5. Preview the swaps; click "Download New CSV" for a
 *      DK-re-uploadable file.
 */
export default function LateSwap() {
  const [csv, setCsv] = useState("");
  const [parsed, setParsed] = useState({ header: [], rows: [] });
  // Sprint 60.140 — Hot/Cold picker replaces the CSV-of-live-scores
  // textarea. "auto" preserves legacy behaviour (injury sweep only,
  // no chase/comeback swaps unless the user pastes scores). "chase"
  // forces Hot mode (swap into chalk to stay top). "comeback" forces
  // Cold mode (swap into leverage to differentiate). Users overwhelmingly
  // preferred a 1-click toggle over pasting a CSV of live fantasy points
  // mid-slate — this is one of those cases where the "right" UX beats
  // the "smart" UX.
  const [modeOverride, setModeOverride] = useState("auto");
  const [locks, setLocks] = useState(new Set());
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Sprint 62.3 — index-based parse: DK repeats column names (RB, RB, WR…),
  // so a name-keyed record would collapse duplicates. Roster cells are the
  // columns after the 4 meta columns (Entry ID, Contest Name, Contest ID,
  // Entry Fee). Showdown exports start the roster with CPT.
  const parseCsv = (raw) => {
    const lines = raw.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return { header: [], rows: [] };
    const split = (line) => line.split(",").map((s) => s.trim().replace(/^"|"$/g, ""));
    const header = split(lines[0]);
    const rows = lines.slice(1).map((line) => {
      const cols = split(line);
      const record = { _cols: cols };
      header.forEach((h, i) => { if (!(h in record)) record[h] = cols[i] || ""; });
      return record;
    });
    return { header, rows };
  };

  const onCsvChange = (v) => {
    setCsv(v);
    setParsed(parseCsv(v));
    setResult(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    onCsvChange(text);
    toast.success(`Parsed ${file.name}`);
  };

  const META_COLS = 4;
  const isShowdown = (parsed.header[META_COLS] || "").toUpperCase() === "CPT";
  const slotCols = isShowdown
    ? ["CPT", "FLEX", "FLEX", "FLEX", "FLEX", "FLEX"]
    : ["QB", "RB", "RB", "WR", "WR", "WR", "TE", "FLEX", "DST"];

  // Sprint 63 — pull saved Smart / Manual portfolios straight in (no DK CSV round-trip).
  const [source, setSource] = useState("csv");
  const [savedLineups, setSavedLineups] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const toCells = (players) => {
    const ordered = sortPlayersDKOrder(players);
    const slots = ["QB", "RB", "RB", "WR", "WR", "WR", "TE", "FLEX", "DST"];
    const cells = {};
    slots.forEach((slot, i) => {
      const p = ordered[i];
      cells[`${slot}${i}`] = p ? `${p.name} (${p.dk_id})` : "";
    });
    return cells;
  };
  const loadSaved = async (which) => {
    setSource(which);
    setResult(null);
    setLoadingSaved(true);
    try {
      const r = which === "manual" ? await api.getRawLineups() : await api.getLineups();
      const raw = r?.lineups || [];
      const rows = raw.map((lu, i) => ({
        entry_id: String(lu.lineup_id ?? i + 1),
        contest_name: which === "manual" ? "Manual Optimizer" : "Smart Optimizer",
        contest_id: "",
        entry_fee: "",
        players: toCells(lu.players || []),
      }));
      setSavedLineups(rows);
      if (!rows.length) toast.info(`No saved ${which === "manual" ? "Manual" : "Smart"} lineups yet — build a portfolio first.`);
      else toast.success(`Loaded ${rows.length} ${which === "manual" ? "Manual" : "Smart"} lineup${rows.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load saved lineups");
      setSavedLineups([]);
    } finally { setLoadingSaved(false); }
  };

  const csvLineups = useMemo(() => {
    if (!parsed.rows.length) return [];
    const metaIdx = { entry: parsed.header.findIndex((h) => /^entry\s*id$/i.test(h)),
                      name: parsed.header.findIndex((h) => /^contest\s*name$/i.test(h)),
                      cid: parsed.header.findIndex((h) => /^contest\s*id$/i.test(h)),
                      fee: parsed.header.findIndex((h) => /^entry\s*fee$/i.test(h)) };
    const rosterStart = Math.max(META_COLS, ...Object.values(metaIdx).map((i) => i + 1));
    return parsed.rows.map((r) => {
      const players = {};
      slotCols.forEach((s, i) => { players[`${s}${i}`] = r._cols[rosterStart + i] || ""; });
      const pick = (i, ...keys) => (i >= 0 ? r._cols[i] : keys.map((k) => r[k]).find(Boolean)) || "";
      return {
        entry_id: pick(metaIdx.entry, "Entry ID", "EntryId", "entry_id"),
        contest_name: pick(metaIdx.name, "Contest Name", "ContestName"),
        contest_id: pick(metaIdx.cid, "Contest ID", "ContestId"),
        entry_fee: pick(metaIdx.fee, "Entry Fee", "EntryFee"),
        players,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed, isShowdown]);
  const lineups = source === "csv" ? csvLineups : savedLineups;

  const runAnalyze = async () => {
    if (!lineups.length) {
      toast.error("Paste a DK entries CSV first");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        lineups,
        // Realized scores kept for backwards compat + power users; the
        // hot/cold toggle now drives the primary flow.
        realized_scores: {},
        locked_games: Array.from(locks),
      };
      if (modeOverride === "chase" || modeOverride === "comeback") {
        payload.mode_override = modeOverride;
      }
      const r = await http.post("/late-swap/csv-analyze", payload);
      setResult(r.data);
      toast.success(`Mode: ${r.data.mode.toUpperCase()} · ${r.data.total_swaps} swap${r.data.total_swaps === 1 ? "" : "s"} across ${r.data.total_lineups} lineup${r.data.total_lineups === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Late swap analysis failed");
    } finally {
      setBusy(false);
    }
  };

  // Sprint 62.2 — REAL late swap: ILP re-solve with locked-game players fixed.
  const runResolve = async () => {
    if (!lineups.length) { toast.error("Paste a DK entries CSV first"); return; }
    setBusy(true);
    try {
      const r = await http.post("/late-swap/resolve", { lineups, locked_games: Array.from(locks) });
      setResult({ ...r.data, mode: "re-solve" });
      toast.success(`Re-solved ${r.data.total_lineups} lineup${r.data.total_lineups === 1 ? "" : "s"} · ${r.data.total_swaps} swaps${r.data.infeasible ? ` · ${r.data.infeasible} infeasible` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Late swap re-solve failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = () => {
    if (!result?.lineups?.length) return;
    const header = ["Entry ID", "Contest Name", "Contest ID", "Entry Fee", ...slotCols];
    const dedupedHeader = ["Entry ID", "Contest Name", "Contest ID", "Entry Fee"];
    // Reflect DK's duplicated position columns (RB,RB,WR,WR,WR)
    slotCols.forEach((s) => dedupedHeader.push(s));
    const csvLines = [dedupedHeader.join(",")];
    result.lineups.forEach((lu) => {
      const cells = [
        lu.entry_id, lu.contest_name || "", lu.contest_id || "", lu.entry_fee || "",
      ];
      slotCols.forEach((s, i) => {
        cells.push((lu.new_players[`${s}${i}`] || "").replace(/,/g, " "));
      });
      csvLines.push(cells.join(","));
    });
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `warpaint-late-swap-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Download started — re-upload this to DK via Manage Entries");
  };

  return (
    <div className="space-y-6" data-testid="page-late-swap">
      <div className="lg:hidden">
        <a href="/swap" data-testid="late-swap-mobile-link"
          className="block w-full py-3 text-center border border-[#39FF14] text-[#39FF14] font-bold uppercase tracking-widest text-[12px]">
          Open the one-handed Sunday Swap screen →
        </a>
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Late Swap</div>
          <h1 className="heading text-3xl font-black uppercase tracking-tighter mt-1">
            Live Portfolio Late Swap
          </h1>
          <p className="text-zinc-300 text-sm mt-2 max-w-3xl">
            Load your saved Smart or Manual portfolio — or the DK entries CSV from Manage Entries — mark
            which games have locked, and hit Run. Locked players stay, scratches are forced out, open slots
            are re-solved on live numbers, and you get a DK-ready CSV back.
          </p>
        </div>
      </div>

      {/* --- Lineup source --- */}
      <section className="bg-[#121212] border border-zinc-800 p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="late-swap-source-picker">
          <UploadSimple size={16} className="text-[#39FF14]" weight="fill" />
          <div className="heading text-lg font-black uppercase">1 · Your lineups</div>
          <div className="flex items-center gap-1 ml-2">
            {[["manual", "Manual Optimizer"], ["smart", "Smart Optimizer"], ["csv", "DK CSV"]].map(([k, label]) => (
              <button key={k} type="button" disabled={loadingSaved}
                onClick={() => (k === "csv" ? (setSource("csv"), setResult(null)) : loadSaved(k))}
                data-testid={`late-swap-source-${k}`} aria-pressed={source === k}
                className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  source === k ? "bg-[#39FF14] text-black border-[#39FF14] font-bold" : "border-zinc-700 text-zinc-300 hover:border-[#39FF14] hover:text-[#39FF14]"}`}>
                {loadingSaved && source === k ? "Loading…" : label}
              </button>
            ))}
          </div>
          {lineups.length > 0 && (
            <span className="mono text-[10px] uppercase tracking-widest text-[#39FF14] border border-[#39FF14]/40 px-2 py-0.5">
              {lineups.length} lineup{lineups.length === 1 ? "" : "s"} {source === "csv" ? "parsed" : "loaded"}
            </span>
          )}
        </div>
        {source !== "csv" && (
          <div className="mono text-[10px] text-zinc-500 mb-2" data-testid="late-swap-saved-note">
            Saved {source === "manual" ? "Manual" : "Smart"} portfolio loaded straight from your account — no export needed.
            Switch to <span className="text-white">DK CSV</span> if you want to swap entries already in a contest.
          </div>
        )}
        {source === "csv" && (<>
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <label className="mono text-[10px] uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-[#39FF14] hover:text-[#39FF14] px-3 py-1.5 cursor-pointer transition-colors">
            <UploadSimple size={11} className="inline mr-1" />
            Upload CSV
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="late-swap-csv-file"
            />
          </label>
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">or paste below</span>
        </div>
        <textarea
          value={csv}
          onChange={(e) => onCsvChange(e.target.value)}
          rows={6}
          placeholder="Entry ID,Contest Name,Contest ID,Entry Fee,QB,RB,RB,WR,WR,WR,TE,FLEX,DST&#10;12345,MAIN Milly,20000,20.00,Josh Allen (1234),..."
          className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2 text-xs mono"
          data-testid="late-swap-csv-textarea"
        />
        </>)}
      </section>

      {/* --- Portfolio Mode + Run --- */}
      <section className="bg-[#121212] border border-zinc-800 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Lightning size={16} className="text-[#39FF14]" weight="fill" />
          <div className="heading text-lg font-black uppercase">2 · Portfolio Status</div>
        </div>
        <p className="text-[11px] mono text-zinc-500 mb-3 leading-relaxed">
          Tell us how your lineups are trending mid-slate and we'll swap accordingly.
          The injury sweep always runs regardless — this only picks the second signal.
        </p>
        <div
          data-testid="late-swap-mode-picker"
          className="grid grid-cols-1 sm:grid-cols-3 gap-2"
        >
          {[
            {
              id: "auto",
              label: "Auto · Injury Sweep Only",
              hint: "Skip hot/cold logic — just replace ruled-out players with next-depth peers.",
              icon: <CheckCircle size={16} weight="fill" />,
              ring: "border-zinc-700 text-zinc-300",
              activeRing: "border-white bg-white/5 text-white",
            },
            {
              id: "chase",
              label: "🔥 Hot · Chase Mode",
              hint: "Portfolio is running above the field. Swap into chalkier plays to protect the lead.",
              icon: <Fire size={16} weight="fill" />,
              ring: "border-[#FFCC00]/40 text-[#FFCC00]",
              activeRing: "border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00]",
            },
            {
              id: "comeback",
              label: "🧊 Cold · Comeback Mode",
              hint: "Portfolio is running below the field. Swap into contrarian leverage to differentiate.",
              icon: <Lightning size={16} weight="fill" />,
              ring: "border-[#007AFF]/40 text-[#7ec8ff]",
              activeRing: "border-[#007AFF] bg-[#007AFF]/10 text-[#7ec8ff]",
            },
          ].map((opt) => {
            const isActive = modeOverride === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={`late-swap-mode-${opt.id}`}
                onClick={() => setModeOverride(opt.id)}
                className={`text-left border-2 px-3 py-3 transition-colors ${isActive ? opt.activeRing : opt.ring} hover:brightness-125`}
                title={opt.hint}
              >
                <div className="flex items-center gap-2 mb-1">
                  {opt.icon}
                  <span className="mono text-[11px] uppercase tracking-widest font-bold">
                    {opt.label}
                  </span>
                </div>
                <div className="text-[10px] leading-snug opacity-80">
                  {opt.hint}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={runAnalyze}
            disabled={busy || !lineups.length}
            data-testid="late-swap-run-btn"
            className="bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[11px] px-5 py-2 inline-flex items-center gap-1"
          >
            <ArrowsClockwise size={12} weight="fill" />
            {busy ? "Analyzing…" : "Quick Swap"}
          </button>
          <button
            onClick={runResolve}
            disabled={busy || !lineups.length}
            data-testid="late-swap-resolve-btn"
            title="Re-run the optimizer on every lineup: locked-game players stay fixed, inactives are forced out, open slots are re-solved on current projections while keeping every lineup unique. Ranked by Field Win %."
            className="bg-[#FFCC00] hover:bg-[#e0b400] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[11px] px-5 py-2 inline-flex items-center gap-1"
          >
            <Lightning size={12} weight="fill" />
            {busy ? "Solving…" : "Re-solve Open Slots (ILP)"}
          </button>
          {result && (
            <button
              onClick={downloadCsv}
              data-testid="late-swap-download-btn"
              className="border border-[#39FF14]/40 hover:bg-[#39FF14] hover:text-black text-[#39FF14] font-black uppercase tracking-widest text-[11px] px-5 py-2 inline-flex items-center gap-1 transition-colors"
            >
              <DownloadSimple size={12} weight="fill" />
              Download New CSV
            </button>
          )}
        </div>
      </section>

      {/* --- Results --- */}
      {result && (
        <section className="bg-[#121212] border border-zinc-800 p-4" data-testid="late-swap-results">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <CheckCircle size={16} className="text-[#39FF14]" weight="fill" />
            <div className="heading text-lg font-black uppercase">3 · Swap Recommendations</div>
            <span className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
              result.mode === "chase"
                ? "text-[#FF8A00] border-[#FF8A00]/40 bg-[#FF8A00]/5"
                : "text-[#39FF14] border-[#39FF14]/40 bg-[#39FF14]/5"
            }`}>
              Mode: {result.mode}
            </span>
            {result.mode !== "re-solve" ? (
              <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                Portfolio {result.portfolio_median} pts vs slate {result.slate_median_estimate} pts
              </span>
            ) : (
              <span className="mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid="late-swap-resolve-summary">
                ILP re-solve · {result.total_swaps} swaps · {result.infeasible || 0} infeasible · ranked by Field Win %
              </span>
            )}
          </div>
          <div className="space-y-3">
            {result.lineups.map((lu) => (
              <div
                key={lu.entry_id}
                data-testid={`late-swap-lineup-${lu.entry_id}`}
                className="border border-zinc-900 bg-black/40 p-3"
              >
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="mono text-[11px] font-bold text-white">Entry {lu.entry_id}</span>
                  <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">{lu.contest_name}</span>
                  {lu.projection != null && (
                    <span className="mono text-[9px] text-zinc-400" data-testid={`late-swap-proj-${lu.entry_id}`}>
                      proj <span className="text-[#39FF14] font-bold">{Number(lu.projection).toFixed(1)}</span>
                      {lu.sim_win_rank ? <> · Field rank <span className="text-[#FFCC00] font-bold">#{lu.sim_win_rank}</span></> : null}
                      {lu.locked?.length ? <> · {lu.locked.length} locked</> : null}
                    </span>
                  )}
                  {lu.status === "infeasible" && (
                    <span className="mono text-[9px] uppercase tracking-widest text-[#FF3B30]">infeasible — kept as-is</span>
                  )}
                  <span className={`ml-auto mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${
                    lu.swap_count > 0
                      ? "text-[#39FF14] border-[#39FF14]/40 bg-[#39FF14]/5"
                      : "text-zinc-500 border-zinc-800"
                  }`}>
                    {lu.swap_count} swap{lu.swap_count === 1 ? "" : "s"}
                  </span>
                </div>
                {lu.swaps.length === 0 ? (
                  <div className="mono text-[10px] text-zinc-600 italic">No swaps recommended — lineup stays as-is.</div>
                ) : (
                  <div className="space-y-1">
                    {lu.swaps.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] mono flex-wrap">
                        <span className="mono text-[8px] uppercase tracking-widest text-[#FF8A00] font-bold w-32 flex-shrink-0">{s.reason}</span>
                        <span className="text-zinc-500 line-through">{s.out.name}</span>
                        <span className="text-zinc-700">→</span>
                        <span className="text-[#39FF14] font-bold">{s.in?.name || "—"}</span>
                        <span className="text-zinc-600 text-[9px]">
                          proj {s.out.proj?.toFixed(1)} → {s.in?.proj?.toFixed(1) ?? "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
